<script setup>
import { computed, watch } from 'vue'
import AppBadge from '@/components/atoms/AppBadge'
import AppButton from '@/components/atoms/AppButton'
import HelpHubGuideStep from './HelpHubGuideStep.vue'
import HelpHubHeading from './HelpHubHeading.vue'
import HelpHubLinkCard from './HelpHubLinkCard.vue'
import HelpHubSection from './HelpHubSection.vue'
import HelpHubStepMarker from './HelpHubStepMarker.vue'
import HelpHubStepProgress from './HelpHubStepProgress.vue'
import HelpHubStepVideo from './HelpHubStepVideo.vue'
import HelpHubTip from './HelpHubTip.vue'
import { useStrapiStore } from '@/stores/StrapiStore'
import { useUserStore } from '@/stores/UserStore'
import { useGetStarted } from '@/hooks/useGetStarted'
import { useHelpHub } from '@/hooks/useHelpHub'
import { isLocalhost } from '@/helpers/env'
import { GETTING_STARTED_SURFACE } from '@/constants'
import videoImage from '@/assets/images/help-hub/get-started-video.jpg'
import mockImage from '@/assets/images/help-hub/connect-ebay-video.jpg'

const props = defineProps({
  // A step, or the page of one of its guide steps: both carry the same fields.
  step: { type: Object, required: true },
  number: { type: Number, required: true },
  // A guide step's page has no number beside its title.
  numbered: { type: Boolean, default: true },
  // The step waits for the first sale: a badge says so where the progress would be.
  locked: Boolean,
  // "What's next?" links to other help pages.
  next: { type: Array, default: () => [] },
})

const strapiStore = useStrapiStore()
const userStore = useUserStore()

// Opening a step's own page (not one of its guide pages) is what starts the step: it reads "In progress" from here on.
watch(() => props.numbered && props.step?.key, (stepKey) => {
  if (stepKey) userStore.startOnboardingStep(stepKey)
}, { immediate: true })
const { stepCount, completedCount, canComplete, completeStep, completing } = useGetStarted()
const { helpLink, appLink } = useHelpHub()

// One guide, or several lists; a list without a title of its own is the step-by-step guide.
const guides = computed(() => props.step.guides ?? [{ title: props.step.guideTitle, steps: props.step.guide }])

// A guide step outlines the page it opens: that page's guide steps that carry a one-line summary.
const outlineOf = (item) => item.page?.guide?.filter((child) => child.summary)

// TODO(before-prod): remove MOCK_SECTION, `mockImage` and the localhost check below; render `step.sections` directly.
// MOCK: formatted copy and a picture, to preview what the CMS can send; on the first step's page, localhost only,
// so staging shows exactly what Strapi returns.
const MOCK_SECTION = {
  title: 'Mock: formatted copy and a picture',
  text: 'Copy can be **bold**, *italic* or [a link](https://support.carddealerpro.com).',
  image: { src: mockImage, alt: 'A phone on a table running Card Dealer Pro, scanning a trading card', caption: 'A caption sits under its picture.' },
}
const sections = computed(() => (isLocalhost() && props.numbered && props.number === 1
  ? [...(props.step.sections ?? []), MOCK_SECTION]
  : props.step.sections))

// The Home welcome video stands in until each step carries its own.
if (!strapiStore.videoTour.videoUrl) strapiStore.fetchVideoTour()
</script>

<template>
  <article class="flex flex-col gap-y-6 px-4 pt-3 pb-6">
    <HelpHubStepProgress v-if="!locked" :number="number" :total="stepCount" :completed="completedCount">
      <!-- A step's own page says when the step is done, and lets the dealer mark it done by hand until then. -->
      <template v-if="numbered" #action>
        <AppBadge v-if="step.completed" pill custom-class="border border-blue-100 text-green-500">
          <Icon icon="heroicons-solid:check-circle" class="size-4" />
          Step completed
        </AppBadge>

        <AppButton
          v-else-if="canComplete(step)"
          variant="soft-outlined"
          size="xs"
          rounded="full"
          leading-icon="tabler:circle"
          icon-class="text-blue-300"
          :loading="completing === step.key"
          @click="completeStep(step, GETTING_STARTED_SURFACE.STEP_PAGE)"
        >
          Mark as complete
        </AppButton>
      </template>
    </HelpHubStepProgress>

    <div class="flex flex-col gap-y-3">
      <AppBadge v-if="locked" size="sm" variant="tint-light" class="self-start">
        <Icon icon="heroicons-outline:lock-closed" class="size-4 shrink-0" />
        Unlocks after your first sale
      </AppBadge>

      <header class="flex flex-col gap-y-2">
        <div class="flex items-center gap-x-3">
          <HelpHubStepMarker v-if="numbered" variant="page" class="flex-none">{{ number }}</HelpHubStepMarker>

          <h3 class="text-lg font-semibold text-gray-900" v-text="step.helpTitle ?? step.title" />
        </div>

        <p class="whitespace-pre-line text-sm text-gray-600" v-text="step.helpDescription" />
      </header>

      <!-- A page without a thumbnail of its own shows the shared one. -->
      <HelpHubStepVideo :image="step.videoImage ?? videoImage" :video="strapiStore.videoTour.videoUrl" :slug="step.key ?? ''" />

      <!-- Opens the step's page in the app; the hub stays on this help page. A page without a route has no button. -->
      <!-- Figma draws the 16px arrow with a 1.5px stroke. -->
      <AppButton
        v-if="step.helpRoute ?? step.route"
        as="router-link"
        :to="appLink(step.helpRoute ?? step.route)"
        variant="secondary"
        trailing-icon="heroicons:arrow-up-right"
        icon-class="[&>path]:stroke-[2.25px]"
      >
        {{ step.helpActionLabel ?? step.title }}
      </AppButton>
    </div>

    <!-- Text that a page puts above its lists. -->
    <HelpHubSection
      v-for="section in step.leadSections"
      :key="section.title"
      :title="section.title"
      :text="section.text"
      :link="section.link"
      :image="section.image"
    />

    <template v-for="(list, index) in guides" :key="index">
      <section class="flex flex-col gap-y-4">
        <!-- A list with a slug opens its own help page from its heading. -->
        <HelpHubHeading :title="list.title ?? 'Step-by-step guide'" :to="list.slug && helpLink(list.slug)" />

        <!-- A guide step with a slug opens its own help page; one with a route, a page in the app. -->
        <ol class="flex flex-col gap-y-3" :start="list.start">
          <HelpHubGuideStep
            v-for="(item, itemIndex) in list.steps"
            :key="item.title"
            :number="(list.start ?? 1) + itemIndex"
            :title="item.title"
            :badge="item.badge"
            :description="item.description"
            :list="item.list"
            :outline="outlineOf(item)"
            :tip="item.tip"
            :tip-title="item.tipTitle"
            :tip-to="item.tipSlug && helpLink(item.tipSlug)"
            :to="item.slug && helpLink(item.slug)"
            :route="item.route && appLink(item.route)"
            :slug="step.key ?? ''"
          />
        </ol>
      </section>

      <!-- A tip that follows this list; it may keep its icon beside its heading and number its points. -->
      <HelpHubTip v-if="list.tip || list.tipList" :title="list.tipTitle" :icon="list.tipIcon" :list="list.tipList">{{ list.tip }}</HelpHubTip>

      <!-- Text that follows this list, before the next one. -->
      <HelpHubSection
        v-for="section in list.sections"
        :key="section.title"
        :title="section.title"
        :text="section.text"
        :link="section.link"
        :image="section.image"
        :to="section.slug && helpLink(section.slug)"
      />
    </template>

    <!-- A section with a slug opens its own help page from its heading; its tip, list and points sit under its copy; a compact one has a 12px heading. -->
    <HelpHubSection
      v-for="section in sections"
      :key="section.title"
      :title="section.title"
      :text="section.text"
      :link="section.link"
      :image="section.image"
      :list="section.list"
      :bullets="section.bullets"
      :compact="section.compact"
      :tip="section.tip"
      :tip-title="section.tipTitle"
      :to="section.slug && helpLink(section.slug)"
    />

    <HelpHubTip v-if="step.tip" :title="step.tipTitle">{{ step.tip }}</HelpHubTip>

    <!-- A grey box that closes the page. -->
    <HelpHubTip v-if="step.note" :title="step.noteTitle" neutral>{{ step.note }}</HelpHubTip>

    <section v-if="next.length" class="flex flex-col gap-y-4">
      <h4 class="text-base font-semibold text-gray-900">What’s next?</h4>

      <ul class="flex flex-col gap-y-3">
        <HelpHubLinkCard
          v-for="item in next"
          :key="item.slug"
          :title="item.title"
          :description="item.description"
          :icon="item.icon"
          :to="helpLink(item.slug)"
        />
      </ul>
    </section>
  </article>
</template>
